import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { useManagedQuery } from 'utils/managedHooks';
import Tabs from 'components/ui/Tabs';
import { GET_USER, GET_CUSTOMER_DETAILS } from 'store/queries';
import { Spinner } from 'admin-components/components';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import { userShape } from 'constants/propTypes';
import { Column, Row } from 'components/shared/Layout';
import { LOYALTY_ACCOUNT_STATUS } from 'constants/clubEurostar';
import { isLoungeStaff } from 'utils/user';

import config from '../../utils/config';

import MemberInfo from './MemberInfo';
import Home from './Home';
import PointsStatement from './PointsStatement';
import AccountDetails from './AccountDetails';
import Subscriptions from './Subscriptions';
import { StyledClubEurostarMemberContainer, ClubEurostarMemberContent } from './ClubEurostarMember.styles';
import TierHistory from './TierHistory';
import Benefits from './Benefits';

const IS_TIER_BENEFIT_UPGRADE_ENABLED = config('ENABLE_TIER_BENEFIT_UPGRADE', { canOverride: true }) === 'true';

const tabs = [
  {
    value: 'account-details',
    label: <FormattedMessage id="clubEurostar.accountDetails" />
  },
  {
    value: 'tier-history',
    label: <FormattedMessage id="clubEurostar.tierHistory" />
  },
  {
    value: 'benefits',
    label: <FormattedMessage id="clubEurostar.benefits" />,
    disabled: !IS_TIER_BENEFIT_UPGRADE_ENABLED
  },
  {
    value: 'member-home',
    label: <FormattedMessage id="clubEurostar.memberHome" />
  },
  {
    value: 'points-statement',
    label: <FormattedMessage id="clubEurostar.pointsStatement" />
  },
  {
    value: 'subscriptions',
    label: <FormattedMessage id="subscriptions.title" />
  }
];

const ClubEurostarMember = ({
  match: {
    params: { membershipNumber }
  },
  user
}) => {
  const isLoungeStaffUser = isLoungeStaff(user);
  const isThalysPassEnabled = config('ENABLE_THALYS_PASS', { canOverride: true }) === 'true';

  const { push, location } = useHistory();

  const {
    data: {
      me: { canManageClubEurostar, canSellTravelPass }
    }
  } = useManagedQuery(GET_USER, {
    fetchPolicy: 'cache-only'
  });

  const { loading, data, error } = useManagedQuery(GET_CUSTOMER_DETAILS, {
    variables: {
      membershipNumber
    },
    fetchPolicy: 'network-only'
  });

  const customerInfo = data && data.getCustomerDetails;
  const accountStatus = customerInfo?.profile?.loyalty?.status;

  const canBuySubscriptions = isThalysPassEnabled && canSellTravelPass;
  const canPerformOperations = [LOYALTY_ACCOUNT_STATUS.ACTIVE, LOYALTY_ACCOUNT_STATUS.MERGED].includes(accountStatus) && !isLoungeStaffUser;

  const tabId = location.hash ? location.hash.substring(1) : tabs[0].value;
  const [selectedTab, setSelectedTab] = useState(tabId);

  if (isLoungeStaffUser ? false : !canManageClubEurostar) {
    push('/');

    return null;
  }

  if (customerInfo && !customerInfo.profile?.loyalty) {
    push('/clubeurostar/manage');

    return null;
  }

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: '/clubeurostar/manage'
    });
  }

  return (
    <StyledClubEurostarMemberContainer>
      {loading && (
        <Row span={16} height="100vh" data-testid="full-page-spinner">
          <Column justifyContent="center" alignItems="center">
            <Spinner />
          </Column>
        </Row>
      )}
      {!loading && (
        <ClubEurostarMemberContent>
          <MemberInfo customerInfo={customerInfo} />
          {!isLoungeStaffUser && (
            <>
              <Tabs onClick={setSelectedTab} selectedValue={selectedTab} tabs={tabs} />
              {selectedTab === tabs[0].value && (
                <AccountDetails
                  customerInfo={customerInfo}
                  canPerformOperations={canPerformOperations}
                  isLoungeStaffUser={isLoungeStaffUser}
                />
              )}
              {selectedTab === tabs[1].value && (
                <TierHistory
                  customerInfo={customerInfo}
                  canPerformOperations={canPerformOperations}
                  isLoungeStaffUser={isLoungeStaffUser}
                />
              )}
              {selectedTab === tabs[2].value && <Benefits customerInfo={customerInfo} />}
              {selectedTab === tabs[3].value && (
                <Home customerInfo={customerInfo} canPerformOperations={canPerformOperations} isLoungeStaffUser={isLoungeStaffUser} />
              )}
              {selectedTab === tabs[4].value && (
                <PointsStatement
                  customerInfo={customerInfo}
                  user={user}
                  canPerformOperations={canPerformOperations}
                  isLoungeStaffUser={isLoungeStaffUser}
                />
              )}
              {selectedTab === tabs[5].value && (
                <Subscriptions
                  customerInfo={customerInfo}
                  canSellTravelPass={canBuySubscriptions}
                  user={user}
                  canPerformOperations={canPerformOperations}
                  isLoungeStaffUser={isLoungeStaffUser}
                />
              )}
            </>
          )}
        </ClubEurostarMemberContent>
      )}
    </StyledClubEurostarMemberContainer>
  );
};

ClubEurostarMember.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      membershipNumber: PropTypes.string
    })
  }),
  user: userShape
};

export default ClubEurostarMember;
